'use client';
import type { Idea } from '@/lib/research/state-machine';
import { SignalDot } from '@/components/SignalDot';

interface Props {
  open: Idea[];
  /** When provided, each row becomes a button that calls this with the
   *  clicked symbol. Used to open the IdeaCard thesis inline. */
  onSelect?: (symbol: string) => void;
  /** Symbol currently expanded above — gets the amber left-border highlight. */
  activeSymbol?: string | null;
  /** Current portfolio holdings. Names already here get "MAINTAIN" label;
   *  others get "OPEN POSITION". 2026-05-23 Steve directive. */
  portfolio?: Set<string>;
}

function daysSince(iso: string): number {
  return Math.max(0, Math.round((Date.now() - Date.parse(iso + 'T12:00:00Z')) / 86_400_000));
}

const convictionTone: Record<string, string> = {
  high: 'text-emerald-300',
  med: 'text-amber-300',
  low: 'text-[#94a3b8]',
};

export function OpenIdeasTable({ open, onSelect, activeSymbol, portfolio }: Props) {
  if (open.length === 0) {
    return (
      <div className="text-[12px] text-[#64748b] italic px-1">
        No open ideas. The desk surfaces new ones with the daily research run.
      </div>
    );
  }
  // Steve directive: "line up the action buttons so they are in a column."
  // Previous flex layout had the action pill floating right after the
  // entrant bubble, so actions slid left/right with name length. Now every
  // row is a fixed grid with the ACTION column at a constant x.
  return (
    <div className="border border-[#1e293b] rounded-md overflow-hidden">
      <div className="grid grid-cols-[78px_minmax(0,1fr)_220px_130px] gap-3 px-3 py-2 text-[9px] uppercase tracking-widest text-[#64748b] font-semibold border-b border-[#1e293b] bg-[#0a0e14]">
        <div>Initiated</div>
        <div>Idea</div>
        <div>Action</div>
        <div className="text-right">Conviction</div>
      </div>
      {open.map((idea) => {
        const isSwap = idea.type === 'swap';
        const days = daysSince(idea.initiationDate);
        const active = activeSymbol === idea.symbol;
        const clickable = !!onSelect;
        const Row = clickable ? 'button' : 'div';
        return (
          <Row
            key={`${idea.symbol}-${idea.initiationDate}`}
            type={clickable ? 'button' : undefined}
            onClick={clickable ? () => onSelect!(idea.symbol) : undefined}
            className={`grid grid-cols-[78px_minmax(0,1fr)_220px_130px] gap-3 items-center text-[11px] px-3 py-1.5 border-b border-[#1e293b]/60 last:border-b-0 text-left w-full transition-colors ${
              active ? 'bg-amber-400/10 border-l-2 border-l-amber-400' : (clickable ? 'hover:bg-[#1e293b]/40 cursor-pointer' : '')
            }`}
          >
            <span className="font-mono text-[10px] text-[#64748b] tabular-nums">
              {idea.initiationDate}
            </span>

            {/* Entrant bubble — recommended addition */}
            <span
              className="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 w-fit"
              title={`${idea.name} · idea opened ${idea.initiationDate}`}
            >
              <span className="font-mono font-bold text-emerald-200">{idea.symbol}</span>
              <SignalDot symbol={idea.symbol} size={6} />
              <span className="text-[10px] text-emerald-300/80 truncate max-w-[180px]">{idea.name}</span>
            </span>

            {isSwap && idea.swapSource ? (
              <span className="inline-flex items-baseline gap-1.5 w-fit">
                <span className="text-[10px] text-[#64748b] uppercase tracking-wider">swap from</span>
                <span
                  className="inline-flex items-baseline gap-1.5 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/5"
                  title={`Source: ${idea.swapSource} (recommendation pending)`}
                >
                  <span className="font-mono font-bold text-amber-300">{idea.swapSource}</span>
                  <span className="text-[10px] text-amber-300/70">held</span>
                </span>
              </span>
            ) : portfolio?.has(idea.symbol) ? (
              <span
                className="inline-flex items-baseline px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 w-fit"
                title="Already held — desk reiterates the position; not a recommendation to add"
              >
                <span className="text-[10px] text-amber-300/90 uppercase tracking-wider font-semibold">maintain</span>
              </span>
            ) : (
              <span className="inline-flex items-baseline px-2 py-0.5 rounded-full bg-emerald-500/5 border border-emerald-500/20 w-fit">
                <span className="text-[10px] text-emerald-300/80 uppercase tracking-wider font-semibold">open position</span>
              </span>
            )}

            <span className={`font-mono tabular-nums text-[10px] text-right ${convictionTone[idea.conviction] ?? 'text-[#94a3b8]'}`}>
              {idea.conviction} · {days}d held
            </span>
          </Row>
        );
      })}
    </div>
  );
}
